import { cn } from '@nextui-org/react'
import React, { useEffect, useState } from 'react'
import { useParams } from 'react-router-dom'
import { Button } from '~/components/common/Button'
import CodeDownloadOptions from '~/components/task/CodeDownloadOptions'
import { usePluginWorkspaceSafe } from '~/contexts/PluginWorkspaceContext'
import { useConversationUuid } from '~/hooks'
import { useCompactViewport } from '~/hooks/useCompactViewport'
import { AgentTaskStatus, CommandType, Diff, DiffType } from '~/protos/agents'
import { bridge } from '~/services/bridge'
import { requestChanges } from '~/services/taskService'
import { BetterToast } from '~/utils/toast'
import { cm } from '~/utils/utils'
import { SimpleTextarea } from '../../common/SimpleTextarea'
import { useGeneralReviewComment, useReviewDiffCommentsWithRef } from './hooks'
import { ReviewHistory } from './ReviewHistory'

interface FileListProps {
  diffs: Diff[] | null
  activeDiff: Diff | null
  onSelectDiff: (diff: Diff) => void
  onAcceptDiff: () => void
  readOnly?: boolean
  /** Show approve button - controlled by parent */
  showApproveButton?: boolean
  /** Show feedback controls (textarea + button) - controlled by parent */
  showFeedbackControls?: boolean
  commandType: CommandType
  taskStatus?: AgentTaskStatus
  tupleIndex?: number
  onDownloadOriginalRepo?: () => void
  /** Whether approval is in progress (persists across tab switches) */
  isApproving?: boolean
  /** Project name resolved from task metadata (informational only) */
  projectName?: string
  /** Task id supplied by the parent surface. Falls back to 0 when unavailable. */
  taskId?: number
  /** Hide the legacy file picker when a parent renders the shared diff viewer. */
  showFileSelector?: boolean
}

/**
 * Presentational component for displaying file list and action controls.
 * All visibility logic is controlled by parent via props.
 */
const FileList: React.FC<FileListProps> = ({
  diffs = null,
  activeDiff,
  onSelectDiff,
  onAcceptDiff,
  readOnly = false,
  showApproveButton = false,
  showFeedbackControls = false,
  commandType,
  taskStatus,
  tupleIndex,
  onDownloadOriginalRepo,
  isApproving = false,
  projectName,
  taskId = 0,
  showFileSelector = true
}) => {
  const isCompactViewport = useCompactViewport()
  const [localDisabled, setIsDisabled] = React.useState<boolean>(false)
  const isDisabled = isApproving || localDisabled || taskStatus !== AgentTaskStatus.DIFF_GENERATED
  const pluginWs = usePluginWorkspaceSafe()
  const isInPlugin = pluginWs !== null
  const getLabel = (diffType: DiffType) => {
    switch (diffType) {
      case DiffType.MODIFY:
        return 'M'
      case DiffType.ADD:
        return 'U'
      case DiffType.DELETE:
        return 'D'
      default:
        return ''
    }
  }

  const getFilePath = (diff: Diff | null) => {
    if (diff == null) return ''
    return diff.diffType === DiffType.DELETE ? diff.aFilePath : diff.bFilePath
  }

  const renderLabel = (diff: Diff) => {
    const label = getLabel(diff.diffType)
    switch (diff.diffType) {
      case DiffType.ADD:
        return <span className='px-1 font-semibold text-[#2d8646]'>{label}</span>
      case DiffType.MODIFY:
        return <span className='px-1 font-semibold text-[#eb5325]'>{label}</span>
      case DiffType.DELETE:
        return <span className='px-1 font-semibold text-[#df2a0f]'>{label}</span>
    }
  }

  const { workspaceUuid } = useParams<{ workspaceUuid: string }>()

  const handleRequestChanges = async () => {
    if (comment.length === 0 && comments.length === 0) {
      BetterToast.error('Comment is required')
      return
    }

    if (!workspaceUuid) {
      BetterToast.error('Workspace not found')
      return
    }

    setIsDisabled(true)

    try {
      await requestChanges({
        workspaceUuid,
        uuid,
        taskId,
        diffReview: {
          generalDiffComment: comment,
          diffComments: comments
        },
        commandType
      })
      BetterToast.success('Requested changes submitted')
    } catch (error) {
      console.error('Error requesting changes:', error)
      BetterToast.error('Failed to request changes')
    } finally {
      setIsDisabled(false)
    }
  }

  const handleAcceptDiff = async () => {
    setIsDisabled(true)
    onAcceptDiff()
  }
  const uuid = useConversationUuid()
  const [comment, setComment] = useGeneralReviewComment()
  const [comments] = useReviewDiffCommentsWithRef()
  const downloadOptions = Array.isArray(diffs) ? (
    <CodeDownloadOptions
      diffs={diffs}
      taskId={taskId}
      workspaceUuid={workspaceUuid}
      conversationUuid={uuid}
      tupleIndex={tupleIndex}
      onDownloadOriginalRepo={onDownloadOriginalRepo}
    />
  ) : null

  return (
    <div className='w-full min-w-0 relative'>
      {/* Figured it may not be obvious that this component is a file selector */}
      <div
        className={
          isCompactViewport
            ? 'flex items-center justify-between pb-2'
            : 'flex items-center justify-between pb-[16px]'
        }
      >
        <div
          className={isCompactViewport ? 'text-[16px] font-semibold' : 'text-[18px] font-semibold'}
        >
          {showFileSelector ? 'Select File' : 'Code Review'}
        </div>
        {Array.isArray(diffs) && (!showFeedbackControls || isInPlugin) && (
          <div className='flex items-center gap-2 caret-transparent'>
            {!showFeedbackControls && downloadOptions}
            {isInPlugin && diffs.length > 0 && (
              <ApplyToIDEButton
                diffs={diffs}
                projects={pluginWs.projects}
                projectName={projectName}
                taskId={taskId}
              />
            )}
          </div>
        )}
      </div>

      <style
        dangerouslySetInnerHTML={{
          __html: `
          .custom-scrollbar::-webkit-scrollbar {
            -webkit-appearance: none;
            width: 6px;
          }
          .custom-scrollbar::-webkit-scrollbar-track {
            background: #f1f5f9;
            border-radius: 3px;
          }
          .custom-scrollbar::-webkit-scrollbar-thumb {
            background: #cbd5e0;
            border-radius: 3px;
          }
          .custom-scrollbar::-webkit-scrollbar-thumb:hover {
            background: #a0aec0;
          }
        `
        }}
      />
      {Array.isArray(diffs) ? (
        <div className='flex flex-col w-full min-w-0'>
          {!readOnly && showFileSelector && <div className='headline-lg-bold mb-2'>Editor</div>}
          {!isInPlugin && (
            <div className='flex items-center gap-1.5 mb-2'>
              <FolderIcon className='w-3.5 h-3.5 text-tokens-neutral-text-tertiary' />
              <span
                className={cn(
                  'text-xs font-medium',
                  projectName
                    ? 'text-tokens-neutral-text-secondary'
                    : 'text-tokens-neutral-text-tertiary'
                )}
              >
                {projectName || 'No Repository'}
              </span>
            </div>
          )}
          {showFileSelector && (
            <ul
              className={cn(
                'w-full max-h-[120px] overflow-y-auto border border-gray-200 rounded-md custom-scrollbar mb-3'
              )}
              style={{
                scrollbarWidth: 'thin',
                scrollbarColor: '#cbd5e0 #f7fafc',
                boxShadow:
                  'inset 0 2px 4px 0 rgba(0, 0, 0, 0.06), inset 0 -2px 4px 0 rgba(0, 0, 0, 0.06)'
              }}
            >
              {diffs.map((diff, index) => (
                <li
                  key={index}
                  onClick={() => onSelectDiff(diff)}
                  className={cm(
                    'rounded-sm text-default-600 p-1.5 cursor-pointer overflow-hidden flex items-center gap-1',
                    'text-ellipsis hover:bg-[#f0f0f0]',
                    getFilePath(diff) == getFilePath(activeDiff) ? 'bg-[#ededed]' : ''
                  )}
                >
                  {renderLabel(diff)}
                  <span className='truncate'>{getFilePath(diff)}</span>
                </li>
              ))}
            </ul>
          )}
          {showFeedbackControls && (
            <div
              className={
                isCompactViewport
                  ? 'flex flex-col gap-3 w-full'
                  : 'grid grid-cols-[minmax(0,1fr)_auto] gap-4 w-full'
              }
            >
              <div className='h-full w-full min-w-0'>
                <SimpleTextarea
                  minRows={4}
                  placeholder='Provide any feedback on the generated code to request changes...'
                  value={comment}
                  onChange={value => setComment(uuid, value)}
                  className='w-full'
                />
              </div>

              <div
                className={isCompactViewport ? 'flex flex-col w-full gap-2' : 'flex flex-col gap-2'}
              >
                {showApproveButton && (
                  <Button
                    variant='primary'
                    onClick={handleAcceptDiff}
                    isDisabled={isDisabled}
                    className={isCompactViewport ? 'w-full' : ''}
                  >
                    Approve
                  </Button>
                )}
                <Button
                  variant='destructive'
                  onClick={handleRequestChanges}
                  isDisabled={isDisabled}
                  className={isCompactViewport ? 'w-full' : ''}
                >
                  Provide Feedback
                </Button>
              </div>
              <div className={isCompactViewport ? 'w-full' : 'col-span-2 w-full'}>
                <ReviewHistory title='Review History' actions={downloadOptions}></ReviewHistory>
              </div>
            </div>
          )}
        </div>
      ) : (
        <div className='empty p-2 text-default-500 select-none'>Loading files...</div>
      )}
    </div>
  )
}

function FolderIcon({ className }: { className?: string }) {
  return (
    <svg
      xmlns='http://www.w3.org/2000/svg'
      viewBox='0 0 20 20'
      fill='currentColor'
      className={className}
    >
      <path d='M3.75 3A1.75 1.75 0 002 4.75v3.26a3.235 3.235 0 011.75-.51h12.5c.644 0 1.245.188 1.75.51V6.75A1.75 1.75 0 0016.25 5h-4.836a.25.25 0 01-.177-.073L9.823 3.513A1.75 1.75 0 008.586 3H3.75zM3.75 9A1.75 1.75 0 002 10.75v4.5c0 .966.784 1.75 1.75 1.75h12.5A1.75 1.75 0 0018 15.25v-4.5A1.75 1.75 0 0016.25 9H3.75z' />
    </svg>
  )
}

interface AppliedState {
  applied: boolean
  rootPath?: string
  asNewProject?: string
}

const APPLIED_STATE_PREFIX = 'curie:appliedToIDE:'

function readAppliedState(key: string): AppliedState | undefined {
  try {
    const raw = sessionStorage.getItem(`${APPLIED_STATE_PREFIX}${key}`)
    return raw ? JSON.parse(raw) : undefined
  } catch {
    return undefined
  }
}

function writeAppliedState(key: string, state: AppliedState) {
  try {
    sessionStorage.setItem(`${APPLIED_STATE_PREFIX}${key}`, JSON.stringify(state))
  } catch {
    /* quota exceeded or unavailable — degrade silently */
  }
}

function ApplyToIDEButton({
  diffs,
  projects,
  projectName,
  taskId
}: {
  diffs: Diff[]
  projects: Array<{ name: string; path: string }>
  projectName?: string
  taskId?: number
}) {
  const storeKey = taskId ? String(taskId) : ''
  const persisted = storeKey ? readAppliedState(storeKey) : undefined

  const [applying, setApplying] = useState(false)
  const [applied, setApplied] = useState(persisted?.applied ?? false)
  const [appliedRootPath, setAppliedRootPath] = useState<string | undefined>(persisted?.rootPath)
  const [appliedAsNewProject, setAppliedAsNewProject] = useState<string | undefined>(
    persisted?.asNewProject
  )

  useEffect(() => {
    if (!storeKey) return
    writeAppliedState(storeKey, {
      applied,
      rootPath: appliedRootPath,
      asNewProject: appliedAsNewProject
    })
  }, [storeKey, applied, appliedRootPath, appliedAsNewProject])

  const isBusy = applying

  const matchedWorkspace = projectName
    ? projects.find(p => p.name === projectName || p.path.endsWith(`/${projectName}`))
    : projects.length === 1
      ? projects[0]
      : null

  const isIdeSource = !!matchedWorkspace

  const sendToIDE = async (rootPath: string | undefined, mappedDiffs: unknown[]) => {
    const result = await bridge.applyCodeChange({
      rootPath,
      diffs: mappedDiffs
    })
    if (!result.success) {
      return { ok: false, error: result.error || 'Bridge call failed' }
    }
    const data = result.data as
      | { status?: string; results?: Array<{ path: string; success: boolean; error?: string }> }
      | undefined
    const failedFiles = data?.results?.filter(r => !r.success) ?? []
    if (failedFiles.length > 0) {
      const firstError = failedFiles[0].error || 'Unknown error'
      return { ok: false, error: `${failedFiles[0].path}: ${firstError}` }
    }
    return { ok: true, error: undefined }
  }

  const handleApplyToIDE = async () => {
    setApplying(true)
    try {
      if (isIdeSource) {
        const { ok, error } = await sendToIDE(
          matchedWorkspace.path,
          diffs.map(d => ({
            diffType: DiffType[d.diffType],
            aFilePath: d.aFilePath,
            bFilePath: d.bFilePath,
            aFileContent: d.aFileContent,
            bFileContent: d.bFileContent
          }))
        )
        if (ok) {
          setAppliedRootPath(matchedWorkspace.path)
          setApplied(true)
        } else {
          BetterToast.error(error || 'Failed to apply changes')
        }
      } else {
        const newProjectName = projectName || `curie-output-${Date.now()}`
        let parentDir = ''
        if (projects.length > 0) {
          parentDir = projects[0].path.substring(0, projects[0].path.lastIndexOf('/'))
        }
        if (!parentDir) {
          const wsInfo = await bridge.getWorkspaceInfo()
          const rootPath = wsInfo.data?.rootPath
          if (rootPath) {
            parentDir = rootPath.substring(0, rootPath.lastIndexOf('/'))
          }
        }
        if (!parentDir) {
          const picked = await bridge.pickFolder('Select a folder to save the new project')
          if (picked.success && picked.data?.path) {
            parentDir = picked.data.path
          } else {
            BetterToast.error(
              picked.error?.includes('cancelled')
                ? 'Folder selection cancelled.'
                : 'No folder selected. Please try again.'
            )
            setApplying(false)
            return
          }
        }
        const targetPath = `${parentDir}/${newProjectName}`
        const addDiffs = diffs
          .map(d => ({
            diffType: 'ADD',
            bFilePath: d.diffType === DiffType.DELETE ? d.aFilePath : d.bFilePath,
            bFileContent: d.diffType === DiffType.DELETE ? '' : d.bFileContent
          }))
          .filter(d => d.bFileContent)

        const importResult = await bridge.importProject(targetPath, newProjectName)
        if (!importResult.success) {
          BetterToast.error(importResult.error || 'Failed to create project in IDE')
          setApplying(false)
          return
        }

        const { ok, error } = await sendToIDE(targetPath, addDiffs)
        if (ok) {
          setAppliedRootPath(targetPath)
          setAppliedAsNewProject(newProjectName)
          setApplied(true)
          BetterToast.success(`Created new project: ${newProjectName}`)
        } else {
          BetterToast.error(error || 'Failed to create project')
        }
      }
    } catch (error) {
      console.error('Error applying to IDE:', error)
      BetterToast.error('Failed to apply changes to IDE')
    } finally {
      setApplying(false)
    }
  }

  const handleUndo = async () => {
    setApplying(true)
    try {
      if (appliedAsNewProject && appliedRootPath) {
        const result = await bridge.removeProject(appliedRootPath, appliedAsNewProject)
        if (result.success) {
          BetterToast.success('Project removed')
          setApplied(false)
          setAppliedAsNewProject(undefined)
        } else {
          BetterToast.error(result.error || 'Failed to remove project')
        }
      } else {
        const rootPath = appliedRootPath
        const undoDiffs = diffs.map(d => {
          const type = DiffType[d.diffType]
          if (type === 'ADD') {
            return { diffType: 'DELETE', aFilePath: d.bFilePath }
          }
          if (type === 'DELETE') {
            return { diffType: 'ADD', bFilePath: d.aFilePath, bFileContent: d.aFileContent }
          }
          return { diffType: 'MODIFY', bFilePath: d.bFilePath, bFileContent: d.aFileContent }
        })

        const { ok, error } = await sendToIDE(rootPath, undoDiffs)
        if (ok) {
          BetterToast.success('Changes reverted')
          setApplied(false)
        } else {
          BetterToast.error(error || 'Failed to undo changes')
        }
      }
    } catch (error) {
      console.error('Error undoing IDE changes:', error)
      BetterToast.error('Failed to undo changes')
    } finally {
      setApplying(false)
    }
  }

  if (applied) {
    return (
      <div className='flex items-center gap-2'>
        <div className='flex items-center gap-1.5 text-success'>
          <svg
            xmlns='http://www.w3.org/2000/svg'
            viewBox='0 0 20 20'
            fill='currentColor'
            className='w-4 h-4'
          >
            <path
              fillRule='evenodd'
              d='M10 18a8 8 0 100-16 8 8 0 000 16zm3.857-9.809a.75.75 0 00-1.214-.882l-3.483 4.79-1.88-1.88a.75.75 0 10-1.06 1.061l2.5 2.5a.75.75 0 001.137-.089l4-5.5z'
              clipRule='evenodd'
            />
          </svg>
          <span className='text-xs font-medium'>Applied</span>
        </div>
        <Button
          variant='outlined'
          onClick={handleUndo}
          isDisabled={isBusy}
          className='whitespace-nowrap'
        >
          {isBusy ? 'Undoing...' : 'Undo'}
        </Button>
      </div>
    )
  }

  return (
    <Button
      variant='primary'
      onClick={handleApplyToIDE}
      isDisabled={isBusy}
      className='whitespace-nowrap'
    >
      {isBusy ? 'Applying...' : isIdeSource ? 'Apply to IDE' : 'Save as New Project'}
    </Button>
  )
}

export default FileList
